'use client'

import React from 'react'
import { Icons } from '@/components/ui/icons'

// ============================================================================
// Types
// ============================================================================

interface InfoCardData {
  title: string
  value: string | number | boolean
  subtitle?: string
  icon?: string
  status?: 'success' | 'warning' | 'error' | 'info'
  metadata?: Record<string, string | number | boolean>
}

interface InfoCardWidgetProps {
  data: InfoCardData
}

// ============================================================================
// Icon Mapping
// ============================================================================

function getIcon(iconName?: string) {
  if (!iconName) return null
  
  const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
    'check': Icons.checkCircle,
    'alert': Icons.alertCircle,
    'info': Icons.info,
    'warning': Icons.alertTriangle,
    'server': Icons.server,
    'network': Icons.activity,
    'clock': Icons.clock,
    'user': Icons.user,
  }
  
  const IconComponent = iconMap[iconName.toLowerCase()]
  return IconComponent || null
}

// ============================================================================
// Status Colors
// ============================================================================

function getStatusColors(status?: string) {
  switch (status) {
    case 'success':
      return {
        bg: 'bg-green-50',
        border: 'border-green-200',
        iconColor: 'text-green-600',
        valueColor: 'text-green-900',
      }
    case 'warning':
      return {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        iconColor: 'text-yellow-600',
        valueColor: 'text-yellow-900',
      }
    case 'error':
      return {
        bg: 'bg-red-50',
        border: 'border-red-200',
        iconColor: 'text-red-600',
        valueColor: 'text-red-900',
      }
    case 'info':
      return {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        iconColor: 'text-blue-600',
        valueColor: 'text-blue-900',
      }
    default:
      return {
        bg: 'bg-white',
        border: 'border-gray-200',
        iconColor: 'text-gray-600',
        valueColor: 'text-gray-900',
      }
  }
}

// ============================================================================
// Info Card Widget
// ============================================================================

export function InfoCardWidget({ data }: InfoCardWidgetProps) {
  const { title, value, subtitle, icon, status, metadata } = data
  const colors = getStatusColors(status)
  const IconComponent = getIcon(icon)

  return (
    <div className={`border rounded-lg p-4 ${colors.bg} ${colors.border}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {IconComponent && <IconComponent className={`h-4 w-4 ${colors.iconColor}`} />}
            <h3 className="text-sm font-medium text-gray-700">{title}</h3>
          </div>
          
          <div className={`text-2xl font-bold ${colors.valueColor} mt-1`}>
            {formatValue(value)}
          </div>
          
          {subtitle && (
            <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
          )}
          
          {metadata && Object.keys(metadata).length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <dl className="grid grid-cols-2 gap-2 text-xs">
                {Object.entries(metadata).slice(0, 4).map(([key, val]) => (
                  <div key={key}>
                    <dt className="text-gray-500 font-medium">{formatKey(key)}</dt>
                    <dd className="text-gray-900 mt-0.5">{String(val)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatValue(value: string | number | boolean): string {
  if (value === null || value === undefined) {
    return '—'
  }
  
  if (typeof value === 'number') {
    // Format large numbers with commas
    if (value >= 1000) {
      return value.toLocaleString()
    }
    return String(value)
  }
  
  return String(value)
}

function formatKey(key: string): string {
  // Convert snake_case or camelCase to Title Case
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
}
